import { zodResolver } from '@hookform/resolvers/zod';
import type { CreatedInvitation } from '@baby-tracker/contracts';
import { invitationRoleSchema, type FamilyRole } from '@baby-tracker/validation';
import { format, parseISO } from 'date-fns';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '../../ui/Button.js';
import { Card } from '../../ui/Card.js';
import { errorText } from '../../ui/error-text.js';
import { FormError } from '../../ui/FormError.js';
import fieldStyles from '../../ui/fields.module.css';
import { SelectField } from '../../ui/SelectField.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { TextField } from '../../ui/TextField.js';
import {
    useCreateInvitation,
    useInvitations,
    useResendInvitation,
    useRevokeInvitation,
} from '../../../hooks/useInvitations.js';
import { useMembers, useRemoveMember, useUpdateMember } from '../../../hooks/useMembers.js';

const roleOptions = [
    { value: 'owner', label: 'Owner' },
    { value: 'caregiver', label: 'Caregiver' },
    { value: 'viewer', label: 'Viewer' },
] as const;

const inviteRoleOptions = [
    { value: 'caregiver', label: 'Caregiver' },
    { value: 'viewer', label: 'Viewer' },
] as const;

const inviteFormSchema = z.object({
    role: invitationRoleSchema,
    email: z.union([z.literal(''), z.email('Enter a valid email address')]),
});

type InviteValues = z.infer<typeof inviteFormSchema>;

function roleLabel(role: FamilyRole): string {
    return roleOptions.find((option) => option.value === role)?.label ?? role;
}

function inviteNotice(email: string | null, emailSent: boolean): string {
    if (email !== null && emailSent) {
        return `Invite sent to ${email}. You can also copy the link.`;
    }
    if (email !== null) {
        return `We could not email ${email}. Copy this link and send it.`;
    }
    return 'Copy this link and send it.';
}

function canShareUrl(url: string): boolean {
    if (typeof navigator.share !== 'function') {
        return false;
    }
    if (typeof navigator.canShare !== 'function') {
        return true;
    }
    return navigator.canShare({ url });
}

function isExpired(expiresAt: string): boolean {
    const expires = Date.parse(expiresAt);
    return Number.isNaN(expires) || expires <= Date.now();
}

export function MembersSection({
    familyId,
    canManage,
    currentUserId,
}: {
    familyId: string;
    canManage: boolean;
    currentUserId: string;
}): ReactElement {
    const members = useMembers(familyId);
    const invitations = useInvitations(familyId, canManage);
    const updateMember = useUpdateMember(familyId);
    const removeMember = useRemoveMember(familyId);
    const createInvitation = useCreateInvitation(familyId);
    const resendInvitation = useResendInvitation(familyId);
    const revokeInvitation = useRevokeInvitation(familyId);
    const [serverError, setServerError] = useState<string | null>(null);
    const [inviteLink, setInviteLink] = useState<string | null>(null);
    const [inviteMessage, setInviteMessage] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const form = useForm<InviteValues>({
        resolver: zodResolver(inviteFormSchema),
        defaultValues: { role: 'caregiver', email: '' },
    });
    const ownerCount = members.data?.filter((member) => member.role === 'owner').length ?? 0;
    const emailValue = useWatch({ control: form.control, name: 'email' });
    const pendingInvites = (invitations.data ?? []).filter(
        (invitation) => invitation.revokedAt === null && invitation.acceptedAt === null,
    );

    function showInvite(created: CreatedInvitation): void {
        setInviteLink(`${window.location.origin}/invite/${created.token}`);
        setInviteMessage(inviteNotice(created.email, created.emailSent));
        setCopied(false);
    }

    async function onInvite(values: InviteValues): Promise<void> {
        setServerError(null);
        try {
            const created = await createInvitation.mutateAsync({
                role: values.role,
                ...(values.email ? { email: values.email } : {}),
            });
            showInvite(created);
            form.reset({ role: 'caregiver', email: '' });
        } catch (error) {
            setServerError(errorText(error));
        }
    }

    async function onInviteAgain(invitationId: string): Promise<void> {
        setServerError(null);
        try {
            showInvite(await resendInvitation.mutateAsync(invitationId));
        } catch (error) {
            setServerError(errorText(error));
        }
    }

    async function shareInvite(): Promise<void> {
        if (inviteLink === null) {
            return;
        }
        try {
            await navigator.share({ url: inviteLink });
        } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                return;
            }
            setServerError('Could not open the share sheet. Copy the link instead.');
        }
    }

    return (
        <Card title="Members">
            {members.isError ? <FormError message={errorText(members.error)} /> : null}
            <ul>
                {members.data?.map((member) => {
                    const lastOwner = member.role === 'owner' && ownerCount <= 1;
                    return (
                        <li key={member.userId}>
                            <p>
                                {member.displayName ?? member.name}{' '}
                                <StatusBadge tone="neutral">{roleLabel(member.role)}</StatusBadge>
                            </p>
                            <p>{member.email}</p>
                            {canManage ? (
                                <div className={fieldStyles.row}>
                                    <SelectField
                                        label={`Role for ${member.name}`}
                                        options={roleOptions}
                                        value={member.role}
                                        disabled={lastOwner || updateMember.isPending}
                                        onChange={(event) => {
                                            const role = event.target.value as FamilyRole;
                                            void updateMember.mutateAsync({
                                                userId: member.userId,
                                                body: { role },
                                            });
                                        }}
                                    />
                                    <Button
                                        variant="danger"
                                        disabled={lastOwner || removeMember.isPending}
                                        onClick={() => {
                                            void removeMember.mutateAsync(member.userId);
                                        }}
                                    >
                                        {member.userId === currentUserId
                                            ? 'Leave family'
                                            : 'Remove'}
                                    </Button>
                                </div>
                            ) : member.userId === currentUserId ? (
                                <Button
                                    variant="secondary"
                                    disabled={removeMember.isPending}
                                    onClick={() => {
                                        void removeMember.mutateAsync(member.userId);
                                    }}
                                >
                                    Leave family
                                </Button>
                            ) : null}
                        </li>
                    );
                })}
            </ul>
            {canManage ? (
                <>
                    <h3>Invite a caregiver</h3>
                    <form
                        className={fieldStyles.stack}
                        noValidate
                        onSubmit={(event) => {
                            void form.handleSubmit(onInvite)(event);
                        }}
                    >
                        <SelectField
                            label="Role"
                            options={inviteRoleOptions}
                            error={form.formState.errors.role?.message}
                            {...form.register('role')}
                        />
                        <TextField
                            label="Email (optional)"
                            type="email"
                            autoComplete="off"
                            hint="We'll email this address, and only that account can accept. Leave this blank to share a link yourself."
                            error={form.formState.errors.email?.message}
                            {...form.register('email')}
                        />
                        <Button type="submit" loading={form.formState.isSubmitting}>
                            {emailValue.trim() === '' ? 'Create invite link' : 'Send invite'}
                        </Button>
                    </form>
                    <FormError message={serverError} />
                    {inviteLink ? (
                        <div className={fieldStyles.stack}>
                            {inviteMessage ? <p>{inviteMessage}</p> : null}
                            <TextField
                                label="Invite link"
                                readOnly
                                value={inviteLink}
                                onChange={() => undefined}
                            />
                            <div className={fieldStyles.row}>
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        void navigator.clipboard.writeText(inviteLink).then(
                                            () => {
                                                setCopied(true);
                                            },
                                            () => {
                                                setCopied(false);
                                            },
                                        );
                                    }}
                                >
                                    {copied ? 'Copied' : 'Copy link'}
                                </Button>
                                {canShareUrl(inviteLink) ? (
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            void shareInvite();
                                        }}
                                    >
                                        Share
                                    </Button>
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                    {pendingInvites.length > 0 ? (
                        <>
                            <h3>Pending invites</h3>
                            <ul>
                                {pendingInvites.map((invitation) => {
                                    const expired = isExpired(invitation.expiresAt);
                                    const who = invitation.email ?? 'Anyone with the link';
                                    const when = format(
                                        parseISO(invitation.expiresAt),
                                        'd MMM yyyy',
                                    );
                                    return (
                                        <li key={invitation.id}>
                                            <p>
                                                {who}{' '}
                                                <StatusBadge tone="neutral">
                                                    {roleLabel(invitation.role)}
                                                </StatusBadge>
                                                {expired ? (
                                                    <StatusBadge tone="danger">Expired</StatusBadge>
                                                ) : null}
                                            </p>
                                            <p>{expired ? `Expired ${when}` : `Expires ${when}`}</p>
                                            <div className={fieldStyles.row}>
                                                {expired ? (
                                                    <Button
                                                        variant="secondary"
                                                        loading={resendInvitation.isPending}
                                                        onClick={() => {
                                                            void onInviteAgain(invitation.id);
                                                        }}
                                                    >
                                                        Invite again
                                                    </Button>
                                                ) : null}
                                                <Button
                                                    variant="ghost"
                                                    disabled={revokeInvitation.isPending}
                                                    onClick={() => {
                                                        void revokeInvitation
                                                            .mutateAsync(invitation.id)
                                                            .catch((error: unknown) => {
                                                                setServerError(errorText(error));
                                                            });
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    ) : null}
                </>
            ) : null}
        </Card>
    );
}
